<script setup lang="ts">
import { computed } from 'vue';

// Props pour contrôler l'affichage
const props = defineProps({
    variant: {
        type: String as () => 'banner' | 'inline' | 'compact',
        default: 'banner'
    }
});

// Dates de la campagne Black Friday (en heure de Paris)
const START_DATE = '2025-11-20T00:00:00';
const END_DATE = '2025-12-01T23:59:59';

// Check si on est en mode dev (pour forcer l'affichage)
//const isDev = (import.meta as any).env?.DEV || false;
const isDev = false;

// Fonction pour obtenir la date/heure actuelle à Paris
function getParisTime(): Date {
    const now = new Date();
    // Convertir en heure de Paris (Europe/Paris)
    const parisTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Paris' }));
    return parisTime;
}

// Vérifier si on est dans la période Black Friday
const isBlackFridayPeriod = computed(() => {
    // En dev, on affiche toujours pour tester
    if (isDev) {
        return true;
    }
    
    // En prod, on vérifie les dates en heure de Paris
    const nowParis = getParisTime();
    const startDate = new Date(START_DATE);
    const endDate = new Date(END_DATE);
    
    return nowParis >= startDate && nowParis <= endDate;
});
</script>

<template>
    <div v-if="isBlackFridayPeriod" class="black-friday" :class="variant">
        <div class="black-friday-content">
            <h3 class="title">BlackFriday 2025</h3>
            <p class="dates">Du 20.11.2025 au 01.12.2025</p>
            <p class="promo">
                <span class="discount">-20%</span> 
                <span class="with-code">avec le code promo</span>
                <span class="code">BF2025</span>
            </p>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.black-friday {
    &.compact {
        width: 100%;
        background: linear-gradient(90deg, #1a1a1a 0%, #2d2d2d 100%);
        color: #fff;
        padding: 8px 15px;
        text-align: center;
        border-bottom: 2px solid #FFD700;
        
        .black-friday-content {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            flex-wrap: wrap;
        }
        
        .title {
            font-family: Montserrat, sans-serif;
            font-size: 14px;
            font-weight: 700;
            margin: 0;
            color: #FFD700;
            text-transform: uppercase;
            letter-spacing: 1px;
        }
        
        .dates {
            font-family: Montserrat, sans-serif;
            font-size: 12px;
            font-weight: 400;
            margin: 0;
            color: #fff;
        }
        
        .promo {
            font-family: Montserrat, sans-serif;
            font-size: 13px;
            font-weight: 600;
            margin: 0;
            display: flex;
            align-items: center;
            gap: 6px;
            
            .discount {
                color: #FF4444;
                font-size: 16px;
                font-weight: 700;
            }
            
            .with-code {
                color: #fff;
                font-size: 11px;
            }
            
            .code {
                background: #FFD700;
                color: #1a1a1a;
                padding: 2px 8px;
                border-radius: 3px;
                font-weight: 700;
                font-size: 13px;
                letter-spacing: 1px;
            }
        }
    }
    
    &.banner {
        width: 100%;
        background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
        color: #fff;
        padding: 20px;
        text-align: center;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        margin: 20px 0;
        border: 2px solid #FFD700;
        
        .black-friday-content {
            max-width: 800px;
            margin: 0 auto;
        }
        
        .title {
            font-family: Montserrat, sans-serif;
            font-size: 32px;
            font-weight: 700;
            margin: 0 0 10px 0;
            color: #FFD700;
            text-transform: uppercase;
            letter-spacing: 2px;
        }
        
        .dates {
            font-family: Montserrat, sans-serif;
            font-size: 18px;
            font-weight: 500;
            margin: 0 0 15px 0;
            color: #fff;
        }
        
        .promo {
            font-family: Montserrat, sans-serif;
            font-size: 20px;
            font-weight: 600;
            margin: 0;
            
            .discount {
                color: #FF4444;
                font-size: 28px;
                font-weight: 700;
                margin-right: 8px;
            }
            
            .with-code {
                color: #fff;
                margin-right: 8px;
            }
            
            .code {
                background: #FFD700;
                color: #1a1a1a;
                padding: 4px 12px;
                border-radius: 4px;
                font-weight: 700;
                font-size: 22px;
                letter-spacing: 1px;
            }
        }
    }
    
    &.inline {
        background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
        color: #fff;
        padding: 15px;
        text-align: center;
        border-radius: 6px;
        border: 2px solid #FFD700;
        margin: 10px 0;
        
        .black-friday-content {
            display: flex;
            flex-direction: column;
            gap: 8px;
        }
        
        .title {
            font-family: Montserrat, sans-serif;
            font-size: 20px;
            font-weight: 700;
            margin: 0;
            color: #FFD700;
            text-transform: uppercase;
            letter-spacing: 1px;
        }
        
        .dates {
            font-family: Montserrat, sans-serif;
            font-size: 14px;
            font-weight: 500;
            margin: 0;
            color: #fff;
        }
        
        .promo {
            font-family: Montserrat, sans-serif;
            font-size: 16px;
            font-weight: 600;
            margin: 0;
            
            .discount {
                color: #FF4444;
                font-size: 20px;
                font-weight: 700;
                margin-right: 6px;
            }
            
            .with-code {
                color: #fff;
                font-size: 14px;
                margin-right: 6px;
            }
            
            .code {
                background: #FFD700;
                color: #1a1a1a;
                padding: 3px 10px;
                border-radius: 4px;
                font-weight: 700;
                font-size: 16px;
                letter-spacing: 1px;
            }
        }
    }
}

@media only screen and (max-width: 768px) {
    .black-friday {
        &.compact {
            padding: 6px 10px;
            
            .black-friday-content {
                gap: 8px;
                flex-direction: column;
            }
            
            .title {
                font-size: 12px;
            }
            
            .dates {
                font-size: 10px;
            }
            
            .promo {
                font-size: 11px;
                gap: 4px;
                
                .discount {
                    font-size: 14px;
                }
                
                .with-code {
                    font-size: 10px;
                }
                
                .code {
                    font-size: 11px;
                    padding: 2px 6px;
                }
            }
        }
        
        &.banner {
            padding: 15px;
            
            .title {
                font-size: 24px;
            }
            
            .dates {
                font-size: 14px;
            }
            
            .promo {
                font-size: 16px;
                display: flex;
                flex-direction: column;
                gap: 8px;
                align-items: center;
                
                .discount {
                    font-size: 22px;
                }
                
                .code {
                    font-size: 18px;
                }
            }
        }
        
        &.inline {
            padding: 12px;
            
            .title {
                font-size: 16px;
            }
            
            .dates {
                font-size: 12px;
            }
            
            .promo {
                font-size: 14px;
                
                .discount {
                    font-size: 16px;
                }
                
                .code {
                    font-size: 14px;
                }
            }
        }
    }
}
</style>
